import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  Calendar,
  dateFnsLocalizer,
  Views,
  type View,
} from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enAU } from "date-fns/locale/en-AU";
import type { TimeEntry } from "../../lib/detectDuplicates";
import type { TeamworkUser } from "../../hooks/useTimesheets";
import "react-big-calendar/lib/css/react-big-calendar.css";
import styles from "./TimesheetCalendar.module.css";

const locales = { "en-AU": enAU };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

export interface TimesheetEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resourceId?: string;
  raw: TimeEntry;
}

interface Props {
  entries: TimeEntry[];
  view: View;
  date: Date;
  onView: (v: View) => void;
  onNavigate: (d: Date) => void;
  resources?: TeamworkUser[];
  onSelectEvent?: (event: TimesheetEvent, target: HTMLElement) => void;
}

function entryToEvent(entry: TimeEntry): TimesheetEvent {
  const dateStr = entry.date;
  const timeStr = (entry as TimeEntry & { "time-start"?: string })[
    "time-start"
  ];
  const hours = parseInt(entry.hours, 10) || 0;
  const minutes = parseInt(entry.minutes, 10) || 0;
  const durationMin = hours * 60 + minutes;

  let start: Date;
  if (dateStr.length === 8 && timeStr) {
    const y = parseInt(dateStr.slice(0, 4), 10);
    const m = parseInt(dateStr.slice(4, 6), 10) - 1;
    const d = parseInt(dateStr.slice(6, 8), 10);
    const [hh, mm] = timeStr.split(":").map((s) => parseInt(s, 10));
    start = new Date(y, m, d, hh || 9, mm || 0);
  } else {
    start = new Date(dateStr);
  }
  const end = new Date(start.getTime() + durationMin * 60000);

  const title =
    entry.description?.trim() ||
    entry["todo-item-name"] ||
    entry["project-name"] ||
    "Time entry";

  return {
    id: entry.id,
    title,
    start,
    end,
    resourceId: entry["person-id"],
    raw: entry,
  };
}

export default function TimesheetCalendar({
  entries,
  view,
  date,
  onView,
  onNavigate,
  resources,
  onSelectEvent,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  const events = useMemo(() => entries.map(entryToEvent), [entries]);

  const totalsByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      const mins =
        (parseInt(e.hours, 10) || 0) * 60 + (parseInt(e.minutes, 10) || 0);
      const d = new Date(e.date);
      const key = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
      map.set(key, (map.get(key) ?? 0) + mins);
    }
    return map;
  }, [entries]);

  const totalsByUser = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      const mins =
        (parseInt(e.hours, 10) || 0) * 60 + (parseInt(e.minutes, 10) || 0);
      map.set(e["person-id"], (map.get(e["person-id"]) ?? 0) + mins);
    }
    return map;
  }, [entries]);

  const formatMins = (m: number) => {
    if (m === 0) return "0h";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h === 0) return `${mm}m`;
    if (mm === 0) return `${h}h`;
    return `${h}h ${mm}m`;
  };

  const dayKey = (d: Date) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

  const scrollToTime = useMemo(() => {
    const d = new Date();
    d.setHours(7, 0, 0, 0);
    return d;
  }, []);

  useEffect(() => {
    setScrolled(false);
  }, [view, date]);

  useEffect(() => {
    if (scrolled) return;
    const el = wrapperRef.current?.querySelector(".rbc-time-content");
    if (el) {
      const slotHeight = 48;
      el.scrollTop = 7 * slotHeight;
      setScrolled(true);
    }
  }, [scrolled, events]);

  const eventPropGetter = useCallback((event: TimesheetEvent) => {
    const billable = event.raw.isbillable === "1";
    return {
      className: billable ? styles.billable : styles.nonBillable,
    };
  }, []);

  const handleSelectEvent = useCallback(
    (event: TimesheetEvent, e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();
      onSelectEvent?.(event, e.currentTarget);
    },
    [onSelectEvent],
  );

  const rbcResources = resources?.map((u) => ({
    resourceId: u.id,
    resourceTitle: `${u["first-name"]} ${u["last-name"]}`,
  }));

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <Calendar
        localizer={localizer}
        events={events}
        view={view}
        date={date}
        onView={onView}
        onNavigate={onNavigate}
        views={[Views.WEEK, Views.DAY]}
        step={30}
        timeslots={2}
        scrollToTime={scrollToTime}
        min={new Date(1970, 0, 1, 0, 0, 0)}
        max={new Date(1970, 0, 1, 23, 59, 0)}
        eventPropGetter={eventPropGetter}
        onSelectEvent={handleSelectEvent}
        resources={rbcResources}
        resourceIdAccessor="resourceId"
        resourceTitleAccessor="resourceTitle"
        components={{
          event: ({ event }: { event: TimesheetEvent }) => (
            <div className={styles.eventInner} title={event.title}>
              <span className={styles.eventTitle}>{event.title}</span>
            </div>
          ),
          week: {
            header: ({ date, label }: { date: Date; label: string }) => {
              const total = totalsByDay.get(dayKey(date)) ?? 0;
              return (
                <div className={styles.colHeader}>
                  <span className={styles.colHeaderLabel}>{label}</span>
                  <span className={styles.colHeaderTotal}>
                    {formatMins(total)}
                  </span>
                </div>
              );
            },
          },
          resourceHeader: ({
            label,
            resource,
          }: {
            label: React.ReactNode;
            resource: { resourceId: string; resourceTitle: string };
          }) => {
            const total = totalsByUser.get(resource.resourceId) ?? 0;
            return (
              <div className={styles.colHeader}>
                <span className={styles.colHeaderLabel}>{label}</span>
                <span className={styles.colHeaderTotal}>
                  {formatMins(total)}
                </span>
              </div>
            );
          },
        }}
      />
    </div>
  );
}
